import React from 'react'
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import { Box, useTheme, Typography, List, ListItem } from "@mui/material";
import { tokens } from "../../../theme";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import v2Api from '../../../utils/v2Api';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const grid = 8;

const getItemStyle = (isDragging, draggableStyle, colors) => ({
    userSelect: "none",
    padding: `${grid}px ${grid * 2}px`,
    margin: `0 0 ${grid}px 0`,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: isDragging ? colors.greenAccent[600] : colors.primary[700],
    border: `1px solid ${colors.primary[100]}`,
    ...draggableStyle
});

const getListStyle = (isDraggingOver, colors) => ({
    border: isDraggingOver ? `2px dashed ${colors.greenAccent[500]}` : `2px solid ${colors.primary[100]}`,
    borderRadius: 4,
    padding: grid,
    backgroundColor: colors.primary[600],
});

const ReorderCourseTestsModal = ({
    courseId,
    courseTests,
    onReorder,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
    setBackdropOpen,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [testList, setTestList] = React.useState([]);
    const [testMap, setTestMap] = React.useState({});

    // for modal
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); };

    const handleSave = async (e) => {
        e.preventDefault();
        setBackdropOpen(true);

        await v2Api.put(`/courses/${courseId}/tests/reorder`, {
            orderedIds: testList,
        }).then(() => {
            setSuccessAlertMessage("Tests reordered successfully");
            setSuccessAlertOpen(true);
            handleClose();
            if (onReorder) onReorder();
        }).catch((error) => {
            setErrorAlertMessage(error.response?.data?.message || "Error: Cannot reorder tests. Please try again.");
            setErrorAlertOpen(true);
        }).finally(() => {
            setBackdropOpen(false);
        });
    };

    React.useEffect(() => {
        if (!courseTests || courseTests.length === 0 || !open) return;

        const map = {};
        for (const test of courseTests) {
            map[test.id] = test;
        }

        const list = courseTests.map((test) => test.id);
        setTestList(list);
        setTestMap(map);
    }, [courseTests, open]);

    const handleOnDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination) return;

        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const items = Array.from(testList);
        const [removed] = items.splice(source.index, 1);
        items.splice(destination.index, 0, removed);

        setTestList(items);
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        maxHeight: "80vh",
        overflowY: "auto",
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        borderRadius: '8px',
    };

    return (
        <div>
            <Button
                type="button"
                variant="contained"
                onClick={handleOpen}
                style={{
                    backgroundColor: colors.primary[300],
                    color: "white",
                    width: "14rem",
                    marginTop: "0.5rem",
                    minHeight: "3rem"
                }}
                size='small'
            >
                Reorder Tests
            </Button>
            <Modal
                aria-labelledby="reorder-tests-modal"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                            Reorder Tests
                        </Typography>
                        <Typography variant="body2" color={colors.primary[300]} mt={1}>
                            Drag and drop to reorder the tests.
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2} width="100%" pt={2}>
                            <Box display="flex" flexDirection="column">
                                {!courseTests || courseTests.length === 0 ? (
                                    <Typography color={colors.primary[450]}>No tests available.</Typography>
                                ) : (
                                    <DragDropContext onDragEnd={handleOnDragEnd}>
                                        <Droppable
                                            droppableId="droppable"
                                            renderClone={(provided, snapshot, rubric) => (
                                                <ListItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style,
                                                        colors
                                                    )}
                                                >
                                                    <DragIndicatorIcon />
                                                    Test
                                                </ListItem>
                                            )}
                                        >
                                            {(provided, snapshot) => (
                                                <List
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    style={getListStyle(snapshot.isDraggingOver, colors)}
                                                >
                                                    {testList.map((testId, index) => {
                                                        const test = testMap[testId];

                                                        return (
                                                            <Draggable
                                                                key={testId}
                                                                draggableId={testId.toString()}
                                                                index={index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <ListItem
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        style={getItemStyle(
                                                                            snapshot.isDragging,
                                                                            provided.draggableProps.style,
                                                                            colors
                                                                        )}
                                                                    >
                                                                        <DragIndicatorIcon sx={{ color: colors.primary[300] }} />
                                                                        <Typography sx={{ wordBreak: 'break-word' }}>
                                                                            {test?.title}
                                                                        </Typography>
                                                                    </ListItem>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </List>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </Box>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                sx={{ width: "fit-content" }}
                                onClick={handleSave}
                                disabled={!testList || testList.length === 0}
                            >
                                Save Order
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    )
}

export default ReorderCourseTestsModal
